import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { Bell, Flame, Heart, MapPin } from "lucide-react";
import { GROUNDY } from "@/components/onboarding/groundyArt";
import { OButton, OC, ScreenFrame, StepHeading, StepLine, TextLink } from "@/components/onboarding/onboardingUi";
import { requestPushPermission } from "@/lib/pushPermission";
import { NOTIFICATIONS_COPY } from "@/lib/permissionCopy";
import {
  recordNotificationsDecline,
  type NotificationsPlacement,
} from "@/lib/notificationsOnboarding";

const REASON_ICONS = [MapPin, Heart, Flame];

/**
 * The one push permission ask inside onboarding. It explains what we send
 * before the system prompt appears, and a decline is recorded so nothing asks
 * again soon.
 */
export function NotificationsScreen({
  variant,
  onDone,
}: {
  variant: NotificationsPlacement;
  onDone: () => void;
}) {
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    try {
      posthog.capture("onboarding_notifications_shown", { variant });
    } catch { /* ignore */ }
  }, [variant]);

  const handleEnable = () => {
    // Fire the ask first so the native popup appears on the same gesture, then
    // report which route the user actually got.
    void requestPushPermission()
      .then((outcome) => {
        try {
          posthog.capture("onboarding_notifications_enable_tapped", { variant, outcome });
        } catch { /* ignore */ }
      })
      .catch(() => {
        try {
          posthog.capture("onboarding_notifications_enable_tapped", { variant, outcome: "settings" });
        } catch { /* ignore */ }
      });
    onDone();
  };

  const handleDecline = () => {
    try {
      posthog.capture("onboarding_notifications_declined", { variant });
    } catch { /* ignore */ }
    recordNotificationsDecline();
    onDone();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="onboarding-notifications-screen">
      <ScreenFrame
        dock={
          <>
            <p className="mb-3" style={{ fontSize: 12.5, color: OC.muted }}>{NOTIFICATIONS_COPY.reassurance}</p>
            <OButton onClick={handleEnable}>{NOTIFICATIONS_COPY.enableCta}</OButton>
            <TextLink onClick={handleDecline}>{NOTIFICATIONS_COPY.declineCta}</TextLink>
          </>
        }
      >
        <img src={GROUNDY.notifications} alt="" aria-hidden="true" className="h-[120px] w-auto mb-4" />
        <StepHeading>{NOTIFICATIONS_COPY.heading}</StepHeading>
        <StepLine>{NOTIFICATIONS_COPY.body}</StepLine>

        {/* Preview of a real push. */}
        <div
          className="mt-5 w-full flex items-start gap-3 p-3.5"
          style={{ backgroundColor: OC.white, border: "1px solid #E4DACA", borderRadius: 16, boxShadow: "0 6px 20px rgba(44,24,16,0.07)" }}
        >
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: OC.tile, color: OC.cta }}>
            <Bell className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div style={{ fontSize: 14, fontWeight: 600, color: OC.text }}>{NOTIFICATIONS_COPY.preview.title} ☕</div>
            <div className="mt-0.5" style={{ fontSize: 13, color: OC.textSec }}>{NOTIFICATIONS_COPY.preview.body}</div>
          </div>
        </div>

        <ul className="mt-5 w-full flex flex-col gap-3">
          {NOTIFICATIONS_COPY.reasons.map((reason, i) => {
            const Icon = REASON_ICONS[i] ?? Bell;
            return (
              <li key={reason} className="flex items-center gap-3" style={{ fontSize: 14, color: OC.textSec }}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: OC.tile }} aria-hidden="true">
                  <Icon size={14} color={OC.cta} />
                </span>
                <span>{reason}</span>
              </li>
            );
          })}
        </ul>
      </ScreenFrame>
    </div>
  );
}

export default NotificationsScreen;
